<section class="cg-admin-testimonials">
  <app-admin-page-header>
    <p description>
      Manage student testimonials for public display. Edit the section heading used on the
      <a routerLink="/testimonials">Testimonials</a> page, then curate individual reviews below.
    </p>
    <ng-container actions>
      <app-admin-button variant="secondary" type="button" (click)="refresh()" [disabled]="loading()">
        Reload
      </app-admin-button>
      @if (showEdit()) {
        <app-admin-button
          variant="accent"
          type="button"
          (click)="saveSectionHeading()"
          [disabled]="!canEdit() || savingSection() || loading()"
        >
          {{ savingSection() ? 'Saving…' : 'Save Changes' }}
        </app-admin-button>
      }
    </ng-container>
  </app-admin-page-header>

  @if (loadError(); as le) {
    <div class="cg-admin-alert cg-admin-alert--warning">{{ le }}</div>
  }
  @if (sectionError(); as se) {
    <div class="cg-admin-alert cg-admin-alert--error">{{ se }}</div>
  }

  @if (loading()) {
    <div class="cg-admin-grid">
      @for (i of [1, 2, 3]; track i) {
        <app-admin-skeleton variant="card" />
      }
    </div>
  } @else {
    <app-admin-card title="Section heading">
      <p class="cg-form-help cg-admin-testimonials__intro">
        Title and subtitle shown on the Testimonials page hero (same fields as homepage testimonials section).
      </p>
      <div class="cg-admin-form-grid cg-admin-form-grid--2">
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Section title</span>
          <input
            class="cg-input"
            [(ngModel)]="sectionForm.testimonialsTitle"
            name="testimonialsTitle"
            placeholder="Student Testimonials"
          />
        </label>
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Section subtitle</span>
          <input
            class="cg-input"
            [(ngModel)]="sectionForm.testimonialsSubtitle"
            name="testimonialsSubtitle"
            placeholder="Hear from students who achieved their study abroad goals with Career Grad Global."
          />
        </label>
      </div>
    </app-admin-card>

    <div class="cg-admin-testimonials__table-card">
      <header class="cg-admin-testimonials__repository-head">
        <div>
          <h2 class="cg-admin-testimonials__repository-title">Testimonial repository</h2>
          <p class="cg-admin-testimonials__repository-meta">
            {{ items().length }} {{ items().length === 1 ? 'TESTIMONIAL' : 'TESTIMONIALS' }} TOTAL
          </p>
        </div>
        @if (showCreate()) {
          <app-admin-button variant="accent" type="button" (click)="openCreate()" [disabled]="!canCreate()">
            + New testimonial
          </app-admin-button>
        }
      </header>

      <app-admin-table>
        <thead>
          <tr>
            <th>Student</th>
            <th>Rating</th>
            <th>University &amp; location</th>
            <th>Review snippet</th>
            <th style="text-align: right">Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (items().length === 0) {
            <tr>
              <td colspan="5">
                <app-admin-empty-state
                  icon="message-square-quote"
                  title="No testimonials yet"
                  description="Add your first student testimonial to show on the marketing site."
                />
              </td>
            </tr>
          } @else {
            @for (item of paginatedItems; track item._id) {
              <tr>
                <td>
                  <div class="cg-admin-testimonials__student">
                    @if (profilePreview(item._id); as previewUrl) {
                      <div class="cg-admin-testimonials__avatar cg-admin-testimonials__avatar--image">
                        <img [src]="previewUrl" [alt]="item.studentName" />
                      </div>
                    } @else {
                      <div class="cg-admin-testimonials__avatar" aria-hidden="true">
                        {{ initials(item.studentName) }}
                      </div>
                    }
                    <div>
                      <strong class="cg-admin-testimonials__student-name">{{ item.studentName }}</strong>
                      <span class="cg-admin-testimonials__student-id">ID: {{ shortId(item._id) }}</span>
                      @if (item.pill?.trim()) {
                        <span class="cg-admin-testimonials__draft-tag">{{ item.pill }}</span>
                      }
                      @if (item.status === 'DRAFT') {
                        <span class="cg-admin-testimonials__draft-tag">Draft</span>
                      }
                    </div>
                  </div>
                </td>
                <td>
                  <div
                    class="cg-admin-testimonials__stars"
                    [attr.aria-label]="item.rating + ' out of 5 stars'"
                  >
                    @for (star of starRange; track star) {
                      <span aria-hidden="true" [class.is-dim]="star > item.rating">★</span>
                    }
                  </div>
                  <span class="cg-admin-testimonials__rating-value">{{ ratingLabel(item.rating) }}</span>
                </td>
                <td>
                  @if (item.university || item.country) {
                    @if (item.university) {
                      <strong class="cg-admin-testimonials__university">{{ item.university }}</strong>
                    }
                    @if (item.country) {
                      <span class="cg-admin-testimonials__location">
                        <app-admin-icon name="map-pin" [size]="14" />
                        {{ item.country }}
                      </span>
                    }
                  } @else {
                    <span class="cg-form-help">—</span>
                  }
                </td>
                <td>
                  <p class="cg-admin-testimonials__review-snippet">
                    "{{ reviewSnippet(item.review) }}"
                  </p>
                </td>
                <td>
                  <div class="cg-admin-table-actions">
                    @if (showEdit()) {
                      <app-admin-button variant="ghost" type="button" (click)="edit(item)" [disabled]="!canEdit()">
                        Edit
                      </app-admin-button>
                    }
                    @if (showDelete()) {
                      <app-admin-button variant="danger" type="button" (click)="remove(item)" [disabled]="!canDelete()">
                        Delete
                      </app-admin-button>
                    }
                  </div>
                </td>
              </tr>
            }
          }
        </tbody>
      </app-admin-table>

      @if (items().length > 0) {
        <div class="cg-admin-testimonials__pagination">
          <span class="cg-admin-testimonials__pagination-label">{{ paginationLabel }}</span>
          <div class="cg-admin-testimonials__pagination-controls">
            <app-admin-button
              variant="ghost"
              type="button"
              (click)="prevPage()"
              [disabled]="!canPrevPage"
              aria-label="Previous page"
            >
              Prev
            </app-admin-button>
            @for (page of visiblePages; track page) {
              <app-admin-button
                variant="ghost"
                type="button"
                (click)="goToPage(page)"
                [class.is-active]="page === tablePage() + 1"
                [attr.aria-label]="'Page ' + page"
                [attr.aria-current]="page === tablePage() + 1 ? 'page' : null"
              >
                {{ page }}
              </app-admin-button>
            }
            <app-admin-button
              variant="ghost"
              type="button"
              (click)="nextPage()"
              [disabled]="!canNextPage"
              aria-label="Next page"
            >
              Next
            </app-admin-button>
          </div>
        </div>
      }
    </div>
  }

  @if (showForm()) {
    <app-admin-modal [title]="editingId() ? 'Edit testimonial' : 'New testimonial'" (closed)="closeForm()">
      <div class="cg-admin-form-grid cg-admin-form-grid--2">
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Student name</span>
          <input class="cg-input" [(ngModel)]="form.studentName" name="studentName" />
        </label>
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Pill label</span>
          <input
            class="cg-input"
            [(ngModel)]="form.pill"
            name="pill"
            placeholder="e.g. Scholarship Success"
          />
        </label>
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Rating (1–5)</span>
          <input
            type="number"
            min="1"
            max="5"
            class="cg-input"
            [(ngModel)]="form.rating"
            name="rating"
          />
        </label>
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Country</span>
          <input class="cg-input" [(ngModel)]="form.country" name="country" />
        </label>
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">University</span>
          <input class="cg-input" [(ngModel)]="form.university" name="university" />
        </label>
        <label class="cg-admin-field cg-admin-field--full">
          <span class="cg-admin-field__label">Course</span>
          <input class="cg-input" [(ngModel)]="form.course" name="course" />
        </label>
        <label class="cg-admin-field cg-admin-field--full">
          <span class="cg-admin-field__label">Review</span>
          <textarea class="cg-textarea" rows="4" [(ngModel)]="form.review" name="review"></textarea>
        </label>
        <div class="cg-admin-field cg-admin-field--full cg-admin-testimonial-photo">
          <span class="cg-admin-field__label">Profile photo</span>
          <p class="cg-form-help">Shown on the public testimonial card. Upload a photo, then click Save.</p>

          <div class="cg-admin-testimonial-photo__preview-panel">
            @if (profilePreviewUrl(); as previewUrl) {
              <div class="cg-admin-story-card__preview cg-admin-testimonial-photo__preview">
                <img [src]="previewUrl" alt="Profile photo preview" />
              </div>
            }

            <div class="cg-admin-testimonial-photo__controls">
              <p class="cg-admin-testimonial-photo__status cg-admin-testimonial-photo__status--{{ profilePhotoStatus() }}">
                {{ profilePhotoStatusLabel() }}
              </p>
              <label>
                <span class="sr-only">Choose image</span>
                <input
                  type="file"
                  class="cg-input"
                  accept="image/*"
                  (change)="onProfileFile($event)"
                />
              </label>
              <div class="flex flex-wrap items-center gap-2">
                @if (showUpload()) {
                  <app-admin-button
                    variant="accent"
                    type="button"
                    (click)="uploadProfile()"
                    [disabled]="!canUpload() || !profileFile || profileUploading()"
                  >
                    {{ profileUploading() ? 'Uploading…' : 'Upload photo' }}
                  </app-admin-button>
                }
                @if ((form.profileImageId || profilePreviewUrl()) && showEdit()) {
                  <app-admin-button variant="secondary" type="button" (click)="clearProfile()" [disabled]="!canEdit()">
                    Remove photo
                  </app-admin-button>
                }
              </div>
              @if (profileUploadMessage(); as msg) {
                <p class="cg-form-help">{{ msg }}</p>
              }
            </div>
          </div>
        </div>
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Status</span>
          <select class="cg-select" [(ngModel)]="form.status" name="status">
            <option value="DRAFT">DRAFT</option>
            <option value="PUBLISHED">PUBLISHED</option>
          </select>
        </label>
      </div>
      @if (error(); as msg) {
        <p class="cg-admin-alert cg-admin-alert--error" style="margin-top: var(--cg-space-4)">{{ msg }}</p>
      }
      <div class="cg-admin-page-header__actions cg-admin-testimonials__modal-actions">
        <app-admin-button variant="cancel" type="button" (click)="closeForm()">Cancel</app-admin-button>
        @if (showSaveModal()) {
          <app-admin-button variant="accent" type="button" (click)="save()" [disabled]="saving()">
            {{ saving() ? 'Saving…' : 'Save' }}
          </app-admin-button>
        }
      </div>
    </app-admin-modal>
  }
</section>
